import { useEffect, useRef, useState } from 'react';

/**
 * N8 (86d3w2kez) part 1 — scroll something into view and ring it briefly, for arrival-from-elsewhere.
 *
 * ⛔ **NEVER `:target` FOR THIS.** The CSS `:target` pseudo-class matches on the document fragment at
 * navigation time, and every surface this is used on renders its posting card AFTER an async fetch —
 * so the node does not exist when `:target` is evaluated and the highlight simply never appears. It
 * fails silently and only on slow connections, which is the worst way for it to fail. A ref plus an
 * effect keyed on `active` waits for the node instead.
 *
 * ⚠️ `prefers-reduced-motion` gets a STATIC ring and an instant jump — not "no highlight". Someone who
 * has asked the OS to stop animating things still needs to be told which card they were sent to; it is
 * the movement that is unwelcome, not the information.
 */
export function useFocusHighlight<T extends HTMLElement>(
  active: boolean,
  durationMs = 1500,
): { ref: React.RefObject<T | null>; isHighlighted: boolean } {
  const ref = useRef<T | null>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (!active) return;

    const node = ref.current;
    if (!node) return;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    node.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    setIsHighlighted(true);

    const timer = setTimeout(() => setIsHighlighted(false), durationMs);
    return () => clearTimeout(timer);
  }, [active, durationMs]);

  return { ref, isHighlighted };
}

/**
 * The ring itself, as classes.
 *
 * `motion-safe:` is what makes the fade honour the OS setting for free — under reduced motion the
 * ring appears and disappears with no transition, which is exactly the static ring the AC asks for.
 * Kept beside the hook so the two cannot drift.
 */
export const FOCUS_HIGHLIGHT_CLASS =
  'ring-2 ring-highlight ring-offset-2 ring-offset-background motion-safe:transition-shadow motion-safe:duration-500';
